// WP-C1.1 (INT-1/2/4, SB-4, S-04 step ⑥, S-39..46 "Calendars"): add-by-URL + the connected-
// calendars list, shared between onboarding step ⑥ and Settings → Calendars (the same reuse
// pattern WhatsAppLinkPanel already established for step ⑤ / Settings → WhatsApp).
import type { CalendarConnectionResponse } from '@pfos/shared';
import { RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { checkboxClass, inputClass, primaryButtonInlineClass } from './FormKit';
import {
  useCalendars,
  useCreateCalendar,
  useDeleteCalendar,
  useEditCalendar,
  useRefreshCalendar,
  useRevokeCalendar,
  useUpgradeCalendarOAuth,
} from '../lib/calendars';
import { ApiError } from '../lib/api';

function lastRefreshedLabel(c: CalendarConnectionResponse): string {
  if (c.lastRefreshError !== null) return `Refresh failed: ${c.lastRefreshError}`;
  if (c.lastRefreshedAt === null) return 'Never refreshed';
  return `Last refreshed ${new Date(c.lastRefreshedAt).toLocaleString()}`;
}

function GrantWriteAccessButton({
  connection,
}: {
  connection: CalendarConnectionResponse;
}): React.JSX.Element {
  const upgrade = useUpgradeCalendarOAuth(connection.id);
  const [error, setError] = useState<string | null>(null);

  return (
    <span className="shrink-0">
      <button
        type="button"
        className="rounded px-2 py-1 text-xs text-primary hover:underline"
        disabled={upgrade.isPending}
        onClick={() => {
          setError(null);
          upgrade.mutate(undefined, {
            onSuccess: (data) => {
              window.location.assign(data.authorizationUrl);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : "Couldn't start re-consent.");
            },
          });
        }}
      >
        Grant write access
      </button>
      {error !== null && <span className="ml-2 text-xs text-danger">{error}</span>}
    </span>
  );
}

function CalendarRow({
  connection,
}: {
  connection: CalendarConnectionResponse;
}): React.JSX.Element {
  const edit = useEditCalendar(connection.id);
  const remove = useDeleteCalendar(connection.id);
  const refresh = useRefreshCalendar(connection.id);
  const revoke = useRevokeCalendar(connection.id);
  // WP-C2.1/C2.2 (INT-3): an OAuth (google/microsoft) row is synced on its own scheduler
  // cadence, not this route's manual-refresh — and it's removed via revoke (SB-4: also
  // best-effort revokes the provider-side token), not the plain delete ICS rows use.
  const isOAuth = connection.provider !== 'ics';

  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <p className="truncate text-ink">
          {connection.label}
          {isOAuth && (
            <span className="ml-2 rounded border border-border px-1.5 py-0.5 text-[10px] uppercase text-muted">
              {connection.provider}
            </span>
          )}
        </p>
        <p
          className={`text-xs ${connection.lastRefreshError !== null ? 'text-danger' : 'text-muted'}`}
        >
          {lastRefreshedLabel(connection)}
        </p>
      </div>
      <label className="flex shrink-0 items-center gap-1 text-xs text-muted">
        <input
          type="checkbox"
          className={checkboxClass}
          checked={connection.includeInAvailability}
          disabled={edit.isPending}
          onChange={(e) => {
            edit.mutate({ includeInAvailability: e.target.checked });
          }}
        />
        In availability
      </label>
      {/* WP-C2.3 (SB-1): shown for every OAuth row, not only ones detected as read-only — the
          API exposes no per-connection write-scope field (see oauthConnectionService.ts;
          OAuthTokenSet.scope is never persisted/returned), so this can't be gated more
          precisely from here. Re-consenting when a connection already has write scope is a
          harmless no-op refresh of its tokens (see startUpgrade/handleCallback). */}
      {isOAuth && <GrantWriteAccessButton connection={connection} />}
      {!isOAuth && (
        <button
          type="button"
          aria-label={`Refresh ${connection.label}`}
          className="shrink-0 rounded p-1 text-muted hover:text-ink"
          disabled={refresh.isPending}
          onClick={() => {
            refresh.mutate();
          }}
        >
          <RefreshCw size={14} />
        </button>
      )}
      {isOAuth ? (
        <button
          type="button"
          className="shrink-0 rounded px-2 py-1 text-xs text-muted hover:text-danger"
          disabled={revoke.isPending}
          onClick={() => {
            revoke.mutate();
          }}
        >
          Disconnect
        </button>
      ) : (
        <button
          type="button"
          aria-label={`Remove ${connection.label}`}
          className="shrink-0 rounded p-1 text-muted hover:text-danger"
          disabled={remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
        >
          <X size={14} />
        </button>
      )}
    </li>
  );
}

export function CalendarConnectPanel(): React.JSX.Element {
  const { data: connections, isLoading } = useCalendars();
  const create = useCreateCalendar();
  const [label, setLabel] = useState('');
  const [icsUrl, setIcsUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addCalendar = (): void => {
    setError(null);
    if (label.trim() === '' || icsUrl.trim() === '') return;
    create.mutate(
      { label: label.trim(), icsUrl: icsUrl.trim() },
      {
        onSuccess: () => {
          setLabel('');
          setIcsUrl('');
        },
        onError: () => {
          setError("Couldn't connect that calendar — check the secret ICS URL and try again.");
        },
      },
    );
  };

  return (
    <div>
      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : connections !== undefined && connections.length > 0 ? (
        <ul className="mb-3 divide-y divide-border">
          {connections.map((c) => (
            <CalendarRow key={c.id} connection={c} />
          ))}
        </ul>
      ) : (
        <p className="mb-3 text-xs text-muted">No calendars connected yet.</p>
      )}

      <p className="mb-2 text-xs text-muted">
        Paste a calendar's <strong>secret ICS feed URL</strong> (Google: "Secret address in iCal
        format"; Outlook/M365: "Publish calendar" link). It's stored encrypted and never shown again
        (INT-1/SB-4).
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          className={`${inputClass} w-40`}
          aria-label="Calendar label"
          placeholder="e.g. Personal"
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
        />
        <input
          className={`${inputClass} min-w-0 flex-1`}
          aria-label="Secret ICS URL"
          placeholder="https://…/basic.ics"
          value={icsUrl}
          onChange={(e) => {
            setIcsUrl(e.target.value);
          }}
        />
        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={create.isPending}
          onClick={addCalendar}
        >
          Connect
        </button>
      </div>
      {error !== null && <p className="mt-2 text-xs text-danger">{error}</p>}
    </div>
  );
}
